import { ChangeEvent } from "react";
import {
  MeetingsValue,
  TSpeakerContent,
} from "../../../services/api/instances/meetingsApi/types";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectMeetingValue, selectSpeakers } from "../redux/selectors";
import { useUpdateStorageValue } from "../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { setMeetingValue } from "../redux/slice";
import { TSpeechActions } from "../types";
import { getUpdatedMeetingValue } from "../helpers/getUpdatedValue";
import { generateFinalResultText } from "../helpers/generateFinalResultText";
import { INITIAL_SPEECH_OBJECT } from "../constants";
import { useTranslation } from "react-i18next";
import { generateId } from "../../../shared/utils/generateId";

export const useTranscriptionEditor = () => {
  const meetingValue = useAppSelector(selectMeetingValue);
  const speakers = useAppSelector(selectSpeakers);
  const { saveValue } = useUpdateStorageValue("meetings", setMeetingValue);
  const { t } = useTranslation();

  const onSelectSpeaker = (
    speaker: Omit<TSpeakerContent, "speech_content" | "isActive"> | null,
    id: string
  ) => {
    if (!meetingValue?.meetings_object || !speaker) return;
    const newTranscription = meetingValue.meetings_object.transcription.map(
      (item) =>
        item.id === id
          ? {
              ...item,
              speaker_group_key: speaker?.speaker_group_key,
              speaker_name: speaker?.speaker_name,
            }
          : item
    );
    const newValue: MeetingsValue = {
      meetings_object: {
        ...meetingValue.meetings_object,
        transcription: newTranscription,
        final_result: generateFinalResultText(
          meetingValue.meetings_object.meeting_info,
          newTranscription
        ),
      },
    };
    saveValue(newValue);
  };

  const handleChangeSpeech = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    id: string
  ) => {
    const newContent = event.target.value;
    if (!meetingValue?.meetings_object) return;
    const newTranscription = meetingValue.meetings_object.transcription.map(
      (item) =>
        item.id === id ? { ...item, speech_content: newContent } : item
    );
    const newValue: MeetingsValue = {
      meetings_object: {
        ...meetingValue.meetings_object,
        transcription: newTranscription,
        final_result: generateFinalResultText(
          meetingValue.meetings_object.meeting_info,
          newTranscription
        ),
      },
    };
    saveValue(newValue, true);
  };

  const handleTranscriptActions = (id: string, type: TSpeechActions) => {
    if (!meetingValue?.meetings_object) return;
    const currentValue = meetingValue.meetings_object;
    let newValue: MeetingsValue | null;
    switch (type) {
      case "add": {
        const currentSpeaker = speakers.findIndex(
          (speaker) => speaker.id === id
        );
        const newSpeaker = {
          ...INITIAL_SPEECH_OBJECT,
          speaker_name: t("meetings.speaker_undefined"),
          id: `speaker_${generateId()}`,
        };
        const newTranscript = currentValue.transcription.toSpliced(
          currentSpeaker + 1,
          0,
          newSpeaker
        );
        newValue = getUpdatedMeetingValue(meetingValue, {
          transcription: newTranscript,
          final_result: generateFinalResultText(
            currentValue.meeting_info,
            newTranscript
          ),
        });
        saveValue(newValue);
        return;
      }
      case "delete": {
        const newTranscript = speakers.filter((speaker) => speaker.id !== id);
        newValue = getUpdatedMeetingValue(meetingValue, {
          transcription: newTranscript,
          final_result: generateFinalResultText(
            currentValue.meeting_info,
            newTranscript
          ),
        });
        saveValue(newValue);
        return;
      }
      default:
        return;
    }
  };

  return {
    onSelectSpeaker,
    handleChangeSpeech,
    handleTranscriptActions,
  };
};
